import { Signal } from "signals";
import Components from "slotworks/components/components";
import SymbolComponent from "slotworks/components/matrix/symbol/symbol-component";
import Layers from "slotworks/graphics/layers/layers";
import SpinRecord from "slotworks/model/gameplay/records/spin-record";
import CancelGroup from "slotworks/utils/contracts/cancel-group";
import Contract from "slotworks/utils/contracts/contract";
import Parallel from "slotworks/utils/contracts/parallel";
import Sequence from "slotworks/utils/contracts/sequence";
import Point from "slotworks/utils/geom/point";
import Rectangle from "slotworks/utils/geom/rectangle";
import { RandomRangeInt } from "slotworks/utils/math/random";
import { Tween } from "slotworks/utils/tween";
import { Easing } from "slotworks/utils/tween";
import ReelComponent from "../../reel-component";

export default class ReelCascader {

    public onCascadeStart: Signal = new Signal();
    public onCascadeEnd: Signal = new Signal();

    private config: any;
    private reel: ReelComponent;
    private allSymbols: SymbolComponent[];
    private cancelGroup: CancelGroup = new CancelGroup();

    constructor(reel: ReelComponent, stops: number[], reelset: string[][]) {
        this.reel = reel;
        this.allSymbols = this.reel.getSymbols();
        this.config = Components.config.matrix.transition;
    }

    public cascadeSymbolsOut(symbols: Point[]): Contract<void> {
        return new Sequence([
            () => Contract.wrap(() => this.onCascadeStart.dispatch(this.reel.index)),
            () => new Parallel(this.createRemoveAnimateContracts(symbols)),
            () => this.spliceSymbols(symbols)
        ]);
    }

    public cascadeSymbolsIn(): Contract<void> {
        return new Sequence([
            () => new Parallel(this.createCascadeInContracts()),
            () => Contract.wrap(() => this.onCascadeEnd.dispatch(this.reel.index))
        ]);
    }

    public skip(spinRecord: SpinRecord): Contract<void> {
        return new Contract(() => {
            this.cancelGroup.cancel();
            this.resetSymbols(spinRecord.stops, spinRecord.reelset);
        });
    }

    private resetSymbols(stops: number[], reelset: string[][]): void {
        this.reel.resetSymbolPositions();
        this.allSymbols.forEach((symbol: SymbolComponent, i: number) => {
            const reelIndex: number = this.reel.index - 1;
            const targetReelset: string[] = reelset[reelIndex];
            const targetStop: number = stops[reelIndex] + i;
            symbol.setSymbol(targetReelset[targetStop]);
            symbol.static();
            symbol.updateTransform();
            symbol.setVisible(true);
        });
    }

    private createRemoveAnimateContracts(symbols: Point[]): Array<() => Contract<void>> {
        const removeAnimateContracts: Array<() => Contract<void>> = [];
        symbols.forEach((point: Point) => removeAnimateContracts.push(() => this.allSymbols[point.y].remove()));
        return removeAnimateContracts;
    }

    private spliceSymbols(symbols: Point[]): Contract<void> {
        const rootRect: Rectangle = Layers.MatrixContent.getPosition(`symbol_${this.reel.index}_${1}`);
        const spacingRect: Rectangle = Layers.MatrixContent.getPosition(`symbol_${this.reel.index}_${2}`);
        const spacingY: number = spacingRect.y - rootRect.y;
        const contract: Contract<void> =  new Contract<void>((resolve: (value?: any) => void) => {
            symbols.forEach((point: Point, count: number) => {
                const symbol: SymbolComponent = this.allSymbols.splice(point.y, 1)[0];
                this.allSymbols.unshift(symbol);
                const newTransform: Rectangle = new Rectangle(rootRect.x, rootRect.y - (spacingY * (count + 1)), rootRect.width, rootRect.height);
                symbol.setTransform(newTransform);
                symbol.updateTransform();
            });
            resolve();
        });

        return this.cancelGroup.add(contract);
    }

    private createCascadeInContracts(): Array<() => Contract<void>> {
        const cascadeInContracts: Array<() => Contract<void>> = [];
        let staggerDelay: number = 0;
        for (let index: number = this.allSymbols.length - 1; index >= 0; index--) {
            const targetRect: Rectangle = Layers.MatrixContent.getPosition(`symbol_${this.reel.index}_${index + 1}`);
            const currentRect: Rectangle = this.allSymbols[index].getTransform();
            if (currentRect.y !== targetRect.y) {
                staggerDelay += RandomRangeInt(this.config.symbolStaggerMin ? this.config.symbolStaggerMin : 0, this.config.symbolStaggerMax ? this.config.symbolStaggerMax : 100);
                cascadeInContracts.push(() => this.cascadeIn(index, staggerDelay));
            }
        }
        return cascadeInContracts;
    }

    private cascadeIn(index: number, delay: number): Contract<void> {
        const symbol: SymbolComponent = this.allSymbols[index];
        symbol.setVisible(true);
        const transformRect: Rectangle = symbol.getTransform().clone();
        const endTransform: Rectangle = Layers.MatrixContent.getPosition(`symbol_${this.reel.index}_${index + 1}`);
        const tween: Tween = new Tween(transformRect);
        tween.to({ y: endTransform.y }, 500);
        tween.delay(delay);
        tween.easing(Easing.Cubic.In);
        tween.onUpdate(() => {
            symbol.setTransform(transformRect);
            symbol.updateTransform();
        });
        return new Contract<void>((resolve: (value?: any) => void) => {
            tween.onComplete(() => resolve(null));
            tween.start();
        });
    }
}
